"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WinMacGlobalInstaller = void 0;
/* --------------------------------------------------------------------------------------------
 *  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ------------------------------------------------------------------------------------------ */
const fs = require("fs");
const os = require("os");
const path = require("path");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const FileUtilities_1 = require("../Utils/FileUtilities");
const InstallIdUtilities_1 = require("../Utils/InstallIdUtilities");
const WebRequestWorkerSingleton_1 = require("../Utils/WebRequestWorkerSingleton");
const VersionResolver_1 = require("./VersionResolver");
const versionUtils = require("./VersionUtilities");
const TypescriptUtilities_1 = require("../Utils/TypescriptUtilities");
const CacheTimeConstants_1 = require("./CacheTimeConstants");
const DotnetCoreAcquisitionWorker_1 = require("./DotnetCoreAcquisitionWorker");
const IGlobalInstaller_1 = require("./IGlobalInstaller");
const RegistryReader_1 = require("./RegistryReader");
const StringConstants_1 = require("./StringConstants");
var validationPromptConstants;
(function (validationPromptConstants) {
    validationPromptConstants.noSignatureMessage = `The .NET Installer file could not be validated. It may be insecure or too new to verify. Would you like to continue installing .NET and accept the risks?`;
    validationPromptConstants.cancelOption = 'Cancel Install';
    validationPromptConstants.allowOption = 'Install Anyways';
})(validationPromptConstants || (validationPromptConstants = {}));
/**
 * @remarks
 * This class manages global .NET SDK installations for windows and mac.
 * Both of these OS's have official installers that we can download and run on the machine.
 * Since Linux does not, it is delegated into its own set of classes.
 */
class WinMacGlobalInstaller extends IGlobalInstaller_1.IGlobalInstaller {
    constructor(context, utilContext, installingVersion, installerUrl, installerHash, executor = null, registryReader = null) {
        super(context, utilContext);
        this.cleanupInstallFiles = true;
        this.invalidIntegrityError = `The integrity of the .NET install file is invalid, or there was no integrity to check and you denied the request to continue with those risks.
We cannot verify our .NET file host at this time. Please try again later or install the SDK manually.`;
        this.installerUrl = installerUrl;
        this.installingVersion = installingVersion;
        this.installerHash = installerHash;
        this.commandRunner = executor !== null && executor !== void 0 ? executor : new CommandExecutor_1.CommandExecutor(context, utilContext);
        this.versionResolver = new VersionResolver_1.VersionResolver(context);
        this.file = new FileUtilities_1.FileUtilities();
        this.webWorker = WebRequestWorkerSingleton_1.WebRequestWorkerSingleton.getInstance();
        this.registry = registryReader !== null && registryReader !== void 0 ? registryReader : new RegistryReader_1.RegistryReader(context, utilContext);
    }
    /**
     * Determines if the given exit code has a user-friendly, self-contained error message
     * that doesn't need additional technical details to be helpful to the user.
     */
    static IsUserFriendlyExitCode(code) {
        return this.InterpretExitCode(code) !== '';
    }
    static InterpretExitCode(code) {
        const reportLogMessage = `Please provide your .NET Installer log (note our privacy notice), which can be found at %temp%.
The file has a name like 'Microsoft_.NET_SDK*.log and should appear in recent files.
This report should be made at https://github.com/dotnet/vscode-dotnet-runtime/issues.`;
        switch (code) {
            case '1':
                return `The .NET SDK installer has failed with a generic failure. ${reportLogMessage}`;
            case '5':
                return `Insufficient permissions are available to install .NET. Please run the installer as an administrator.`;
            case '67':
                return `The network name cannot be found to install .NET. ${reportLogMessage}`;
            case '112':
                return `The disk is full. Please free up space and try again.`;
            case '255':
                return `The .NET Installer was terminated by another process unexpectedly. Please try again.`;
            case '1260':
                return `The .NET SDK Install is blocked by group policy. For more information, contact your system administrator.`;
            case '1460':
                return `The .NET SDK had a timeout error. ${reportLogMessage}`;
            case '1603':
                return `Fatal error during .NET SDK installation. ${reportLogMessage}`;
            case '1618':
                return `Another installation is already in progress. Complete that installation before proceeding with this install.`;
            case '000751':
                return `.NET Installer Failed: A page fault was satisfied by reading from a secondary storage device. ${reportLogMessage}`;
            case '2147500037':
                return `.NET Installer Failed: An unspecified error occurred. ${reportLogMessage}`;
            case '2147942405':
                return `Insufficient permissions are available to install .NET. Please try again as an administrator.`;
            case StringConstants_1.UNABLE_TO_ACQUIRE_GLOBAL_LOCK_ERR:
                return `Could not acquire global lock to edit machine state. Was another operation in progress? Try restarting VS Code.`;
        }
        return '';
    }
    installSDK(installation) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, TypescriptUtilities_1.executeWithLock)(this.acquisitionContext.eventStream, false, (0, StringConstants_1.GLOBAL_INSTALL_STATE_MODIFIER_LOCK)(this.acquisitionContext.installDirectoryProvider, installation), CacheTimeConstants_1.GLOBAL_LOCK_PING_DURATION_MS, this.acquisitionContext.timeoutSeconds * 1000, (install) => __awaiter(this, void 0, void 0, function* () {
                // Check for conflicting windows installs
                if (os.platform() === 'win32') {
                    const conflictingVersion = yield this.GlobalWindowsInstallWithConflictingVersionAlreadyExists(this.installingVersion);
                    if (conflictingVersion !== '') {
                        if (conflictingVersion === this.installingVersion) {
                            // The install already exists, we can just exit with Ok.
                            this.acquisitionContext.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionAlreadyInstalled(install, (this.acquisitionContext.acquisitionContext && this.acquisitionContext.acquisitionContext.requestingExtensionId)
                                ? this.acquisitionContext.acquisitionContext.requestingExtensionId : null));
                            return '0';
                        }
                        const err = new EventStreamEvents_1.DotnetConflictingGlobalWindowsInstallError(new EventStreamEvents_1.EventCancellationError('DotnetConflictingGlobalWindowsInstallError', `A global install is already on the machine: version ${conflictingVersion}, that conflicts with the requested version.
                    Please uninstall this version first if you would like to continue.
                    If Visual Studio is installed, you may need to use the VS Setup Window to uninstall the SDK component.`), install);
                        this.acquisitionContext.eventStream.post(err);
                        throw err.error;
                    }
                }
                const installerFile = yield this.downloadInstaller(this.installerUrl);
                const canContinue = yield this.installerFileHasValidIntegrity(installerFile);
                if (!canContinue) {
                    const err = new EventStreamEvents_1.DotnetConflictingGlobalWindowsInstallError(new EventStreamEvents_1.EventCancellationError('DotnetConflictingGlobalWindowsInstallError', this.invalidIntegrityError), install);
                    this.acquisitionContext.eventStream.post(err);
                    throw err.error;
                }
                const installerResult = yield this.executeInstall(installerFile);
                return this.handleStatus(installerResult, installerFile, install);
            }), installation);
        });
    }
    handleStatus(installerResult_1, installerFile_1, install_1) {
        return __awaiter(this, arguments, void 0, function* (installerResult, installerFile, install, allowRetry = true) {
            const validInstallerStatusCodes = ['0', '1641', '3010']; // Ok, Pending Reboot, + Reboot Starting Now
            const noPermissionStatusCodes = ['1', '5', '1260', '2147942405'];
            if (validInstallerStatusCodes.includes(installerResult)) {
                if (this.cleanupInstallFiles) {
                    yield this.file.wipeDirectory(path.dirname(installerFile), this.acquisitionContext.eventStream);
                }
                return '0'; // These statuses are a success, we don't want to throw.
            }
            else if (installerResult === '1602') {
                // Special code for when user cancels the install
                const err = new EventStreamEvents_1.DotnetInstallCancelledByUserError(new EventStreamEvents_1.EventCancellationError('DotnetInstallCancelledByUserError', `The install of .NET was cancelled by the user. Aborting.`), install);
                this.acquisitionContext.eventStream.post(err);
                throw err.error;
            }
            else if (noPermissionStatusCodes.includes(installerResult) && allowRetry) {
                const retryWithElevationResult = yield this.executeInstall(installerFile, true);
                return this.handleStatus(retryWithElevationResult, installerFile, install, false);
            }
            else {
                return installerResult;
            }
        });
    }
    uninstallSDK(installation) {
        return __awaiter(this, void 0, void 0, function* () {
            if (os.platform() === 'win32') {
                const installerFile = yield this.downloadInstaller(this.installerUrl);
                const canContinue = yield this.installerFileHasValidIntegrity(installerFile);
                if (!canContinue) {
                    const err = new EventStreamEvents_1.DotnetConflictingGlobalWindowsInstallError(new EventStreamEvents_1.EventCancellationError('DotnetConflictingGlobalWindowsInstallError', this.invalidIntegrityError), installation);
                    this.acquisitionContext.eventStream.post(err);
                    throw err.error;
                }
                const command = `${path.resolve(installerFile)}`;
                const uninstallArgs = ['/uninstall', '/passive', '/norestart'];
                const commandResult = yield this.commandRunner.execute(CommandExecutor_1.CommandExecutor.makeCommand(command, uninstallArgs), { timeout: this.acquisitionContext.timeoutSeconds * 1000 }, false);
                this.handleTimeout(commandResult);
                return commandResult.status;
            }
            else {
                const macPath = yield this.getMacPath();
                const command = CommandExecutor_1.CommandExecutor.makeCommand(`rm`, [`-rf`, `${path.join(path.dirname(macPath), 'sdk', installation.version)}`, `&&`,
                    `rm`, `-rf`, `${path.join(path.dirname(macPath), 'sdk-manifests', installation.version)}`], true);
                const commandResult = yield this.commandRunner.execute(command, { timeout: this.acquisitionContext.timeoutSeconds * 1000 }, false);
                this.handleTimeout(commandResult);
                return commandResult.status;
            }
        });
    }
    /**
     *
     * @param installerUrl the url of the installer to download.
     * @returns the path to the installer which was downloaded into a directory managed by us.
     */
    downloadInstaller(installerUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            const ourInstallerDownloadFolder = IGlobalInstaller_1.IGlobalInstaller.getDownloadedInstallFilesFolder(installerUrl);
            const installerPath = path.join(ourInstallerDownloadFolder, `${installerUrl.split('/').slice(-1)}`);
            if ((yield this.file.exists(installerPath)) && (yield this.installerFileHasValidIntegrity(installerPath, false))) {
                this.acquisitionContext.eventStream.post(new EventStreamEvents_1.DotnetFileIntegrityCheckEvent(`The installer file ${installerPath} already exists and is valid.`));
                return installerPath;
            }
            yield this.file.wipeDirectory(ourInstallerDownloadFolder, this.acquisitionContext.eventStream);
            const installerDir = path.dirname(installerPath);
            if (!(yield this.file.exists(installerDir))) {
                yield fs.promises.mkdir(installerDir, { recursive: true });
            }
            yield this.webWorker.downloadFile(installerUrl, installerPath, this.acquisitionContext);
            try {
                if (os.platform() === 'win32') // Windows does not have chmod +x ability with nodejs.
                 {
                    const permissionsCommand = CommandExecutor_1.CommandExecutor.makeCommand('icacls', [`"${installerPath}"`, '/grant:r', `"%username%":F`, '/t', '/c']);
                    const commandRes = yield this.commandRunner.execute(permissionsCommand, {}, false);
                    if (commandRes.stderr !== '') {
                        const error = new EventStreamEvents_1.EventBasedError('FailedToSetInstallerPermissions', `Failed to set icacls permissions on the installer file ${installerPath}. ${commandRes.stderr}`);
                        this.acquisitionContext.eventStream.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, error.message));
                    }
                }
                else {
                    yield fs.promises.chmod(installerPath, 0o744);
                }
            }
            catch (error) {
                this.acquisitionContext.eventStream.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, `Failed to chmod +x on ${installerPath}.`));
            }
            return installerPath;
        });
    }
    userChoosesToContinueWithInvalidHash() {
        return __awaiter(this, void 0, void 0, function* () {
            const yes = validationPromptConstants.allowOption;
            const no = validationPromptConstants.cancelOption;
            const message = validationPromptConstants.noSignatureMessage;
            const pick = yield this.utilityContext.ui.getModalWarningResponse(message, no, yes);
            const userConsentsToContinue = pick === yes;
            this.acquisitionContext.eventStream.post(new EventStreamEvents_1.DotnetFileIntegrityCheckEvent(`The valid hash could not be found. The user chose to continue? ${userConsentsToContinue}`));
            return userConsentsToContinue;
        });
    }
    installerFileHasValidIntegrity(installerFile_1) {
        return __awaiter(this, arguments, void 0, function* (installerFile, ask = false) {
            var _a, _b;
            try {
                const realFileHash = yield this.file.getFileHash(installerFile);
                this.acquisitionContext.eventStream.post(new EventStreamEvents_1.DotnetFileIntegrityCheckEvent(`The hash of the installer file we downloaded is ${realFileHash}`));
                const expectedFileHash = this.installerHash;
                this.acquisitionContext.eventStream.post(new EventStreamEvents_1.DotnetFileIntegrityCheckEvent(`The valid and expected hash of the installer file is ${expectedFileHash}`));
                if (expectedFileHash === null) {
                    return ask ? yield this.userChoosesToContinueWithInvalidHash() : false;
                }
                if (realFileHash !== expectedFileHash) {
                    this.acquisitionContext.eventStream.post(new EventStreamEvents_1.DotnetFileIntegrityCheckEvent(`The hashes DO NOT match.`));
                    return false;
                }
                else {
                    this.acquisitionContext.eventStream.post(new EventStreamEvents_1.DotnetFileIntegrityCheckEvent(`This file is valid.`));
                    return true;
                }
            }
            catch (error) {
                // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if ((_a = error === null || error === void 0 ? void 0 : error.message) === null || _a === void 0 ? void 0 : _a.includes('ENOENT')) {
                    this.acquisitionContext.eventStream.post(new EventStreamEvents_1.DotnetFileIntegrityFailureEvent(`The file ${installerFile} was not found, so we couldn't verify it.
Please try again, or download the .NET Installer file yourself. You may also report your issue at https://github.com/dotnet/vscode-dotnet-runtime/issues.`));
                }
                // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                else if ((_b = error === null || error === void 0 ? void 0 : error.message) === null || _b === void 0 ? void 0 : _b.includes('EPERM')) {
                    this.acquisitionContext.eventStream.post(new EventStreamEvents_1.DotnetFileIntegrityFailureEvent(`The file ${installerFile} did not have the correct permissions scope to be assessed.
Permissions: ${JSON.stringify(yield this.commandRunner.execute(CommandExecutor_1.CommandExecutor.makeCommand('icacls', [`"${installerFile}"`]), { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.SYSTEM_INFORMATION_CACHE_DURATION_MS }, false))}`));
                }
                return ask ? this.userChoosesToContinueWithInvalidHash() : false;
            }
        });
    }
    // async is needed to match the interface even if we don't use await.
    // eslint-disable-next-line @typescript-eslint/require-await
    getExpectedGlobalSDKPath(specificSDKVersionInstalled_1, installedArch_1) {
        return __awaiter(this, arguments, void 0, function* (specificSDKVersionInstalled, installedArch, macPathShouldExist = true) {
            if (os.platform() === 'win32') {
                // The program files should always be set, but in the off chance they are wiped, we can try to use the default as backup.
                // Both ia32 and x64 machines will use 'Program Files'
                if (process.env.programfiles) {
                    if (os.arch() === 'arm64' && installedArch === 'x64') {
                        return path.resolve(path.join(process.env.programfiles, 'dotnet', 'x64', 'dotnet.exe'));
                    }
                    return path.resolve(path.join(process.env.programfiles, 'dotnet', 'dotnet.exe'));
                }
                if (os.arch() === 'arm64' && installedArch === 'x64') {
                    return path.resolve(`C:\\Program Files\\dotnet\\x64\\dotnet.exe`);
                }
                return path.resolve(`C:\\Program Files\\dotnet\\dotnet.exe`);
            }
            else if (os.platform() === 'darwin') {
                const sdkPath = yield this.getMacPath(macPathShouldExist);
                return sdkPath;
            }
            const err = new EventStreamEvents_1.DotnetUnexpectedInstallerOSError(new EventStreamEvents_1.EventBasedError('DotnetUnexpectedInstallerOSError', `The operating system ${os.platform()} is unsupported.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.acquisitionContext));
            this.acquisitionContext.eventStream.post(err);
            throw err.error;
        });
    }
    handleTimeout(commandResult) {
        if (commandResult.status === 'SIGTERM') {
            const noResponseError = new EventStreamEvents_1.DotnetNoInstallerResponseError(new EventStreamEvents_1.EventBasedError('DotnetNoInstallerResponseError', `The .NET Installer did not complete after ${this.acquisitionContext.timeoutSeconds} seconds.
If you would like to install .NET, please proceed to interact with the .NET Installer pop-up.
If you were waiting for the install to succeed, please extend the timeout setting of the .NET Install Tool extension.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.acquisitionContext));
            this.acquisitionContext.eventStream.post(noResponseError);
            throw noResponseError.error;
        }
    }
    getMacPath() {
        return __awaiter(this, arguments, void 0, function* (macPathShouldExist = true) {
            const standardHostPath = path.resolve(`/usr/local/share/dotnet/dotnet`);
            const arm64EmulationHostPath = path.resolve(`/usr/local/share/dotnet/x64/dotnet`);
            if ((os.arch() === 'x64' || os.arch() === 'ia32') && (yield (0, TypescriptUtilities_1.getOSArch)(this.commandRunner)).includes('arm') && ((yield this.file.exists(arm64EmulationHostPath)) || !macPathShouldExist)) {
                // VS Code runs on an emulated version of node which will return x64 or use x86 emulation for ARM devices.
                // os.arch() returns the architecture of the node binary, not the system architecture, so it will not report arm on an arm device.
                return arm64EmulationHostPath;
            }
            if (!macPathShouldExist || (yield this.file.exists(standardHostPath)) || !(yield this.file.exists(arm64EmulationHostPath))) {
                return standardHostPath;
            }
            return arm64EmulationHostPath;
        });
    }
    darwinInstallBackup(installerPath) {
        return __awaiter(this, void 0, void 0, function* () {
            // The -W flag makes it so we wait for the installer .pkg to exit, though we are unable to get the exit code.
            const possibleCommands = [
                CommandExecutor_1.CommandExecutor.makeCommand(`command`, [`-v`, `open`]),
                CommandExecutor_1.CommandExecutor.makeCommand(`/usr/bin/open`, [])
            ];
            let workingCommand = yield this.commandRunner.tryFindWorkingCommand(possibleCommands);
            if (!workingCommand) {
                const error = new EventStreamEvents_1.EventBasedError('OSXOpenNotAvailableError', `The 'open' command on OSX was not detected. This is likely due to the PATH environment variable on your system being clobbered by another program.
Please correct your PATH variable or make sure the 'open' utility is installed so .NET can properly execute.`);
                this.acquisitionContext.eventStream.post(new EventStreamEvents_1.OSXOpenNotAvailableError(error, (0, InstallIdUtilities_1.getInstallFromContext)(this.acquisitionContext)));
                throw error;
            }
            else if (workingCommand.commandRoot === 'command') {
                workingCommand = CommandExecutor_1.CommandExecutor.makeCommand(`open`, [`-W`, `"${path.resolve(installerPath)}"`]);
            }
            this.acquisitionContext.eventStream.post(new EventStreamEvents_1.NetInstallerBeginExecutionEvent(`The OS X .NET Installer has been launched.`));
            const commandResult = yield this.commandRunner.execute(workingCommand, { timeout: this.acquisitionContext.timeoutSeconds * 1000 }, false);
            this.acquisitionContext.eventStream.post(new EventStreamEvents_1.NetInstallerEndExecutionEvent(`The OS X .NET Installer has closed.`));
            this.handleTimeout(commandResult);
            return commandResult.status;
        });
    }
    /**
     *
     * @param installerPath The path to the installer file to run.
     * @returns The exit result from running the global install.
     */
    executeInstall(installerPath_1) {
        return __awaiter(this, arguments, void 0, function* (installerPath, elevateVsCode = false) {
            var _a, _b, _c;
            if (os.platform() === 'darwin') {
                // For Mac:
                const sudoInstallerCommand = CommandExecutor_1.CommandExecutor.makeCommand('installer', ['-pkg', `"${path.resolve(installerPath)}"`, '-target', '/'], true);
                this.acquisitionContext.eventStream.post(new EventStreamEvents_1.NetInstallerBeginExecutionEvent(`The OS X .NET Installer has been launched.`));
                const installerResult = yield this.commandRunner.execute(sudoInstallerCommand);
                this.acquisitionContext.eventStream.post(new EventStreamEvents_1.NetInstallerEndExecutionEvent(`The OS X .NET Installer has closed.`));
                this.handleTimeout(installerResult);
                if (installerResult.status !== '0') {
                    // Try to have the user manually go through the installation process
                    // Osascript has some issues running the installer for a pkg, and open does not have an exit code besides 0 if the user cancels/denies.
                    // For understanding the success rates, we can subtract the MacInstallerFailure amount but add back in MacInstallerBackupSuccess for when the user does succeed.
                    // This prevents counting a user who leaves the PC as a failure.
                    // The error code from the installer will give us a better understanding of the installer issues and not count them as an issue in the VS Code setup logic
                    this.acquisitionContext.eventStream.post(new EventStreamEvents_1.MacInstallerFailure(`The installer failed.`, installerResult.status, installerResult.stderr, installerResult.stdout));
                    yield this.darwinInstallBackup(installerPath);
                    const expectedDotnetHostPath = yield this.getExpectedGlobalSDKPath(this.acquisitionContext.acquisitionContext.version, (_a = this.acquisitionContext.acquisitionContext.architecture) !== null && _a !== void 0 ? _a : DotnetCoreAcquisitionWorker_1.DotnetCoreAcquisitionWorker.defaultArchitecture());
                    const expectedInstall = (0, InstallIdUtilities_1.getInstallFromContext)(this.acquisitionContext);
                    const validatedInstall = this.acquisitionContext.installationValidator.validateDotnetInstall(expectedInstall, expectedDotnetHostPath, false, false);
                    if (validatedInstall) {
                        this.acquisitionContext.eventStream.post(new EventStreamEvents_1.MacInstallerBackupSuccess(`The installer succeeded when invoked manually.`));
                        return '0';
                    }
                    else {
                        // Add this back to the failure count as it gets accounted for in the platform agnostic logic
                        this.acquisitionContext.eventStream.post(new EventStreamEvents_1.MacInstallerBackupFailure(`The installer also failed when invoked manually.`));
                    }
                }
                return installerResult.status;
            }
            else {
                const command = `"${path.resolve(installerPath)}"`;
                let commandOptions = [];
                if (yield this.file.isElevated(this.acquisitionContext, this.utilityContext)) {
                    commandOptions = [`/quiet`, `/install`, `/norestart`];
                }
                else {
                    commandOptions = [`/passive`, `/install`, `/norestart`];
                }
                this.acquisitionContext.eventStream.post(new EventStreamEvents_1.NetInstallerBeginExecutionEvent(`The Windows .NET Installer has been launched.`));
                try {
                    const commandResult = yield this.commandRunner.execute(CommandExecutor_1.CommandExecutor.makeCommand(command, commandOptions, elevateVsCode), { timeout: this.acquisitionContext.timeoutSeconds * 1000 }, false);
                    this.handleTimeout(commandResult);
                    this.acquisitionContext.eventStream.post(new EventStreamEvents_1.NetInstallerEndExecutionEvent(`The Windows .NET Installer has closed.`));
                    return commandResult.status;
                }
                catch (error) {
                    // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    if ((_b = error === null || error === void 0 ? void 0 : error.message) === null || _b === void 0 ? void 0 : _b.includes('EPERM')) {
                        // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        error.message = `The installer does not have permission to execute. Please try running as an administrator. ${error === null || error === void 0 ? void 0 : error.message}.
Permissions: ${JSON.stringify(yield this.commandRunner.execute(CommandExecutor_1.CommandExecutor.makeCommand('icacls', [`"${installerPath}"`]), { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.SYSTEM_INFORMATION_CACHE_DURATION_MS }, false))}`;
                    }
                    // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    else if ((_c = error === null || error === void 0 ? void 0 : error.message) === null || _c === void 0 ? void 0 : _c.includes('ENOENT')) {
                        // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        error.message = `The .NET Installation files were not found. Please try again. ${error === null || error === void 0 ? void 0 : error.message}`;
                    }
                    throw error;
                }
            }
        });
    }
    /**
     *
     * @returns Returns '' if no conflicting version was found on the machine.
     * Returns the existing version if a global install with the requested version already exists.
     * OR: If a global install exists for the same band with a higher version.
     * For non-windows cases: In Mac the installer is always shown so that will show users this. For Linux, it's handled by the distro specific code.
     */
    GlobalWindowsInstallWithConflictingVersionAlreadyExists(requestedVersion) {
        return __awaiter(this, void 0, void 0, function* () {
            // Note that we could be more intelligent here and consider only if the SDKs conflict within an architecture, but for now we won't do this.
            const sdks = yield this.registry.getGlobalSdkVersionsInstalledOnMachine();
            for (const sdk of sdks) {
                if ( // Side by side installs of the same major.minor and band can cause issues in some cases. So we decided to just not allow it unless upgrading to a newer patch version.
                // The installer can catch this but we can avoid unnecessary work this way,
                // and for windows the installer may never appear to the user. With this approach, we don't need to handle installer error codes.
                Number(versionUtils.getMajorMinor(requestedVersion, this.acquisitionContext.eventStream, this.acquisitionContext)) ===
                    Number(versionUtils.getMajorMinor(sdk, this.acquisitionContext.eventStream, this.acquisitionContext)) &&
                    Number(versionUtils.getFeatureBandFromVersion(requestedVersion, this.acquisitionContext.eventStream, this.acquisitionContext)) ===
                        Number(versionUtils.getFeatureBandFromVersion(sdk, this.acquisitionContext.eventStream, this.acquisitionContext)) &&
                    Number(versionUtils.getFeatureBandPatchVersion(requestedVersion, this.acquisitionContext.eventStream, this.acquisitionContext)) <=
                        Number(versionUtils.getFeatureBandPatchVersion(sdk, this.acquisitionContext.eventStream, this.acquisitionContext))) {
                    return sdk;
                }
            }
            return '';
        });
    }
}
exports.WinMacGlobalInstaller = WinMacGlobalInstaller;
//# sourceMappingURL=WinMacGlobalInstaller.js.map